import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listMatters,
  listMatterClients,
  createMatter,
  updateMatter,
  archiveMatter,
  unarchiveMatter,
  deleteMatter,
  getMatter,
  MatterListQuery,
  MatterCreatePayload,
  MatterPatch,
} from '@/api/client';
import type { Matter } from '@/types';
import { useAppStore } from '@/store';
import {
  Plus, Search, Loader2, AlertTriangle, Archive, ArchiveRestore,
  Clock, Trash2, Edit3, Check, X, FolderKanban, ArrowLeft,
} from 'lucide-react';

import { apiErrorText } from '../lib/describeApiError';
import CoursesLink from '@/components/layout/CoursesLink';
/**
 * /matters — dedicated matter-list page with filter, sort, pagination.
 * Separate from the Home dashboard (which stays intact as the
 * "get-started-fast" entry point). Uses native HTML throughout so we
 * don't fight the shadcn forwardRef typings.
 */

interface Props {
  onBack?: () => void;
  /** The user-guide videos. */
  onOpenCourses?: () => void;
  /** Open the Drawing workbench for the just-clicked matter. The card
   * button only appears on matters whose claims are frozen
   * (matter.claims_frozen_at non-null). */
  onOpenDrawings?: () => void;
  /** Open the Specification-draft intake for a matter whose claims and
   * drawings already exist. Also gates the "Specification draft"
   * create button — without a handler there is nowhere to send them. */
  onOpenSpecIntake?: (matterId: string) => void;
}

type SortKey = 'updated_at' | 'created_at' | 'title' | 'docket_id' | 'client_name' | 'filing_deadline';

type ArchivedFilter = 'false' | 'true' | 'only';

const inputClass =
  'flex h-9 w-full rounded-md border border-zinc-300 bg-white px-3 py-1.5 ' +
  'text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-50';

const selectClass =
  'flex h-9 rounded-md border border-zinc-300 bg-white px-2 py-1.5 ' +
  'text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:opacity-50';

const primaryBtn =
  'inline-flex h-9 items-center justify-center gap-2 rounded-sm bg-[#002FA7] px-4 ' +
  'text-sm font-medium text-white hover:bg-[#002277] disabled:opacity-50 transition-colors';

const outlineBtn =
  'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-zinc-300 ' +
  'bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors';

function stageLabel(stage: string): string {
  const map: Record<string, string> = {
    stage_1: 'Stage 1',
    stage_2: 'Stage 2',
    stage_2_5: 'Stage 2.5',
    stage_3: 'Stage 3',
    stage_4: 'Stage 4',
  };
  return map[stage] || stage;
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}

function NewMatterForm({ onCreated, onCancel, entryMode }: {
  onCreated: (matter: Matter) => void;
  onCancel: () => void;
  /** 'spec_draft' starts the matter on the Specification-draft intake:
   * the claims and drawings already exist and only the specification is
   * to be drafted. Undefined is the six-stage road. */
  entryMode?: 'spec_draft';
}) {
  const [form, setForm] = useState<MatterCreatePayload>({ title: '', jurisdiction: 'EP' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const upd = (k: keyof MatterCreatePayload) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.title.trim()) {
      setErr('Title is required');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const payload: MatterCreatePayload = {
        title: form.title.trim(),
        jurisdiction: form.jurisdiction,
        ...(entryMode ? { entry_mode: entryMode } : {}),
      };
      (['docket_id', 'client_name', 'client_reference', 'invention_title',
        'technology_area', 'tags', 'filing_deadline', 'priority_date'] as const).forEach(k => {
        const v = (form as any)[k];
        if (typeof v === 'string' && v.trim()) (payload as any)[k] = v.trim();
      });
      const matter = await createMatter(payload);
      onCreated(matter);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to create matter'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="mb-6 border border-zinc-200 rounded-sm bg-white p-6"
      data-testid="matters-new-form"
    >
      <h3 className="text-sm font-semibold text-zinc-900 mb-4">
        {entryMode === 'spec_draft'
          ? 'New specification draft' : 'Create New Matter'}
      </h3>
      {entryMode === 'spec_draft' && (
        <p className="-mt-2 mb-4 text-xs text-zinc-500" data-testid="matters-new-spec-draft-note">
          For a matter whose claims and drawings already exist. You will be
          asked for the disclosure, the claim set and the figures, and then
          only the specification is drafted.
        </p>
      )}
      {err && (
        <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle size={14} />
          {err}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs text-zinc-500 block mb-1">Matter Title *</label>
          <input
            data-testid="matters-new-title"
            className={inputClass}
            value={form.title}
            onChange={upd('title')}
            autoFocus
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Jurisdiction *</label>
          <select
            data-testid="matters-new-jurisdiction"
            className={selectClass + ' w-full'}
            value={form.jurisdiction}
            onChange={upd('jurisdiction')}
          >
            <option value="EP">EP</option>
            <option value="US">US</option>
            <option value="IN">IN</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Docket ID (blank = auto)</label>
          <input
            data-testid="matters-new-docket"
            className={inputClass}
            value={form.docket_id || ''}
            onChange={upd('docket_id')}
            placeholder="e.g. MAT-2026-042"
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Client Name</label>
          <input
            data-testid="matters-new-client"
            className={inputClass}
            value={form.client_name || ''}
            onChange={upd('client_name')}
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Client Reference</label>
          <input
            className={inputClass}
            value={form.client_reference || ''}
            onChange={upd('client_reference')}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs text-zinc-500 block mb-1">Invention Title</label>
          <input
            className={inputClass}
            value={form.invention_title || ''}
            onChange={upd('invention_title')}
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Technology Area</label>
          <input
            className={inputClass}
            value={form.technology_area || ''}
            onChange={upd('technology_area')}
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Tags (comma-separated)</label>
          <input
            className={inputClass}
            value={form.tags || ''}
            onChange={upd('tags')}
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Filing Deadline</label>
          <input
            type="date"
            className={inputClass}
            value={form.filing_deadline || ''}
            onChange={upd('filing_deadline')}
          />
        </div>
        <div>
          <label className="text-xs text-zinc-500 block mb-1">Priority Date</label>
          <input
            type="date"
            className={inputClass}
            value={form.priority_date || ''}
            onChange={upd('priority_date')}
          />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <button
          data-testid="matters-new-submit"
          className={primaryBtn}
          onClick={submit}
          disabled={saving || !form.title.trim()}
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Create
        </button>
        <button
          className={outlineBtn}
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function EditableRow({
  matter, onPatched, onClose,
}: {
  matter: Matter & Record<string, any>;
  onPatched: (m: Matter) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<MatterPatch>({
    title: matter.title || '',
    jurisdiction: matter.jurisdiction,
    docket_id: matter.docket_id || '',
    client_name: matter.client_name || '',
    client_reference: matter.client_reference || '',
    invention_title: matter.invention_title || '',
    technology_area: matter.technology_area || '',
    tags: matter.tags || '',
    filing_deadline: matter.filing_deadline || '',
    priority_date: matter.priority_date || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const claimsFrozen = Boolean(matter.claims_frozen_at);

  const set = (k: keyof MatterPatch) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setErr(null);
    try {
      const updated = await updateMatter(matter.id, form);
      onPatched(updated);
      onClose();
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 border-t border-zinc-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {err && (
        <div className="sm:col-span-2 p-2 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
          <AlertTriangle size={14} />
          {err}
        </div>
      )}
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Title</label>
        <input className={inputClass} value={form.title || ''} onChange={set('title')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">
          Jurisdiction{claimsFrozen && ' (locked — claims frozen)'}
        </label>
        <select
          className={selectClass + ' w-full'}
          value={form.jurisdiction || 'EP'}
          onChange={set('jurisdiction')}
          disabled={claimsFrozen}
          title={claimsFrozen ? 'Locked — claims already frozen' : ''}
        >
          <option value="EP">EP</option>
          <option value="US">US</option>
          <option value="IN">IN</option>
        </select>
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Docket ID</label>
        <input className={inputClass} value={form.docket_id || ''} onChange={set('docket_id')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Client Name</label>
        <input className={inputClass} value={form.client_name || ''} onChange={set('client_name')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Client Reference</label>
        <input className={inputClass} value={form.client_reference || ''} onChange={set('client_reference')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Invention Title</label>
        <input className={inputClass} value={form.invention_title || ''} onChange={set('invention_title')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Technology Area</label>
        <input className={inputClass} value={form.technology_area || ''} onChange={set('technology_area')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Tags</label>
        <input className={inputClass} value={form.tags || ''} onChange={set('tags')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Filing Deadline</label>
        <input type="date" className={inputClass} value={form.filing_deadline || ''} onChange={set('filing_deadline')} />
      </div>
      <div>
        <label className="text-xs text-zinc-500 block mb-1">Priority Date</label>
        <input type="date" className={inputClass} value={form.priority_date || ''} onChange={set('priority_date')} />
      </div>
      <div className="sm:col-span-2 flex gap-2">
        <button className={primaryBtn} onClick={save} disabled={saving}>
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
          Save
        </button>
        <button className={outlineBtn} onClick={onClose} disabled={saving}>
          <X size={14} /> Cancel
        </button>
      </div>
    </div>
  );
}

export default function MattersPage({
  onBack, onOpenDrawings, onOpenSpecIntake, onOpenCourses,
}: Props) {
  const { setMatterId, setMatterState, setActiveStage } = useAppStore();
  const [matters, setMatters] = useState<Matter[]>([]);
  const [total, setTotal] = useState(0);
  const [clients, setClients] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [query, setQuery] = useState<MatterListQuery>({
    search: '',
    client: '',
    status: '',
    jurisdiction: '',
    archived: 'false',
    sort: 'updated_at',
    order: 'desc',
    page: 1,
    per_page: 20,
  });

  const [showNew, setShowNew] = useState(false);
  const [showSpecDraft, setShowSpecDraft] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const data = await listMatters(query);
      setMatters(data.matters || []);
      setTotal(data.total ?? (data.matters?.length ?? 0));
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to load matters'));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const data = await listMatterClients();
        setClients(data.clients || []);
      } catch {
        // Informational filter only — silent fallback.
      }
    })();
  }, []);

  const setField = <K extends keyof MatterListQuery>(k: K, v: MatterListQuery[K]) =>
    setQuery(q => ({ ...q, [k]: v, page: k === 'page' ? (v as number) : 1 }));

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / (query.per_page || 20))),
    [total, query.per_page],
  );

  /** Load a matter into the store. Every route that shows matter state
   * goes through here — leaving it out sets the id while the store
   * still holds the previously opened matter, and the next view to
   * read `matterState` renders one matter's state under another's id. */
  const loadMatter = async (id: string) => {
    try {
      const full = await getMatter(id);
      setMatterId(id);
      setMatterState(full);
      // Explicit land-on-current_stage; setMatterState no longer
      // auto-syncs activeStage, so Stage 3 refreshes don't snap
      // back to Stage 2/2.5.
      setActiveStage(full.matter.current_stage);
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to open matter'));
    }
  };

  /** Open the row itself. A Specification-draft matter has no stages
   * to walk, so it goes back to its intake — which resumes on the
   * first unfinished step — rather than to the stage workflow, where
   * it would land on an empty stage_0 with no way on. This is the
   * row-click route only: the Drawings button below wants the
   * workbench, not the intake, and loads the matter directly. */
  const openMatter = async (id: string) => {
    const row = matters.find((m) => m.id === id);
    if (row?.entry_mode === 'spec_draft' && onOpenSpecIntake) {
      setMatterId(id);
      onOpenSpecIntake(id);
      return;
    }
    await loadMatter(id);
  };

  const onArchive = async (m: Matter & Record<string, any>) => {
    setBusyId(m.id);
    setErr(null);
    try {
      if (m.archived) {
        await unarchiveMatter(m.id);
      } else {
        await archiveMatter(m.id);
      }
      await load();
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to toggle archive'));
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (m: Matter) => {
    if (confirmDeleteId !== m.id) {
      setConfirmDeleteId(m.id);
      return;
    }
    setBusyId(m.id);
    setErr(null);
    try {
      await deleteMatter(m.id);
      setMatters(prev => prev.filter(x => x.id !== m.id));
      setTotal(t => Math.max(0, t - 1));
    } catch (e: any) {
      setErr(apiErrorText(e, 'Failed to delete'));
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-zinc-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderKanban size={20} className="text-[#002FA7]" />
            <span
              className="text-lg font-semibold tracking-tight text-zinc-900"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Matters
            </span>
          </div>
          <div className="flex items-center gap-2">
            <CoursesLink onOpenCourses={onOpenCourses} testId="matters-courses-button" />
            {onBack && (
              <button className={outlineBtn} onClick={onBack} data-testid="matters-back-button">
                <ArrowLeft size={14} /> Back
              </button>
            )}
            {/* The second road in. A matter whose claims and drawings
                already exist skips straight to the specification, so it
                gets its own button rather than a mode buried inside the
                ordinary create form. */}
            {onOpenSpecIntake && (
              <button
                className={outlineBtn}
                onClick={() => { setShowNew(false); setShowSpecDraft(v => !v); }}
                data-testid="matters-new-spec-draft-button"
                title="Claims and drawings already exist — draft only the specification"
              >
                <Plus size={14} /> Specification draft
              </button>
            )}
            <button
              className={primaryBtn}
              onClick={() => { setShowSpecDraft(false); setShowNew(v => !v); }}
              data-testid="matters-new-button"
            >
              <Plus size={14} /> New Matter
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        {err && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-sm text-red-800 flex items-center gap-2">
            <AlertTriangle size={16} />
            {err}
          </div>
        )}

        {showNew && (
          <NewMatterForm
            onCancel={() => setShowNew(false)}
            onCreated={async (m) => {
              setShowNew(false);
              await load();
              // Open the newly created matter straight into the workflow —
              // matches the Home dashboard's behaviour.
              await openMatter(m.id);
            }}
          />
        )}

        {showSpecDraft && (
          <NewMatterForm
            entryMode="spec_draft"
            onCancel={() => setShowSpecDraft(false)}
            onCreated={async (m) => {
              setShowSpecDraft(false);
              await load();
              // Straight into the intake, not the stage workflow — this
              // matter has no stages to walk.
              setMatterId(m.id);
              onOpenSpecIntake?.(m.id);
            }}
          />
        )}

        {/* Filter bar */}
        <div
          className="mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2"
          data-testid="matters-filter-bar"
        >
          <div className="lg:col-span-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-400" />
              <input
                data-testid="matters-search"
                className={inputClass + ' pl-8'}
                value={query.search || ''}
                onChange={e => setField('search', e.target.value)}
                placeholder="Search title, docket, client…"
              />
            </div>
          </div>
          <select
            data-testid="matters-filter-client"
            className={selectClass}
            value={query.client || ''}
            onChange={e => setField('client', e.target.value)}
          >
            <option value="">All clients</option>
            {clients.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            data-testid="matters-filter-jurisdiction"
            className={selectClass}
            value={query.jurisdiction || ''}
            onChange={e => setField('jurisdiction', e.target.value)}
          >
            <option value="">All jurisdictions</option>
            <option value="EP">EP</option>
            <option value="US">US</option>
            <option value="IN">IN</option>
          </select>
          <select
            data-testid="matters-filter-archived"
            className={selectClass}
            value={query.archived || 'false'}
            onChange={e => setField('archived', e.target.value as ArchivedFilter)}
          >
            <option value="false">Active only</option>
            <option value="true">Include archived</option>
            <option value="only">Archived only</option>
          </select>
          <select
            data-testid="matters-filter-sort"
            className={selectClass}
            value={`${query.sort}:${query.order}`}
            onChange={e => {
              const [sort, order] = e.target.value.split(':') as [SortKey, 'asc' | 'desc'];
              setQuery(q => ({ ...q, sort, order, page: 1 }));
            }}
          >
            <option value="updated_at:desc">Recently updated</option>
            <option value="created_at:desc">Newest first</option>
            <option value="created_at:asc">Oldest first</option>
            <option value="title:asc">Title (A→Z)</option>
            <option value="docket_id:asc">Docket (A→Z)</option>
            <option value="client_name:asc">Client (A→Z)</option>
            <option value="filing_deadline:asc">Deadline (soonest)</option>
          </select>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-10 justify-center">
            <Loader2 size={14} className="animate-spin" />
            Loading matters…
          </div>
        ) : matters.length === 0 ? (
          <div className="py-16 text-center text-sm text-zinc-500" data-testid="matters-empty">
            No matters match these filters.
          </div>
        ) : (
          <div className="space-y-2">
            {matters.map((m: any) => {
              const isEditing = editingId === m.id;
              const armed = confirmDeleteId === m.id;
              const busy = busyId === m.id;
              return (
                <div
                  key={m.id}
                  data-testid={`matters-row-${m.id}`}
                  className={
                    'border rounded-sm bg-white p-4 transition-colors ' +
                    (m.archived
                      ? 'border-zinc-300 bg-zinc-50'
                      : 'border-zinc-200 hover:border-zinc-300')
                  }
                >
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => openMatter(m.id)}
                      className="flex-1 text-left"
                      data-testid={`matters-open-${m.id}`}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-zinc-900">
                          {m.docket_id ? (
                            <span className="font-mono text-xs text-zinc-500 mr-2">{m.docket_id}</span>
                          ) : null}
                          {m.title}
                        </span>
                        <span className="px-2 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-xs text-zinc-600">
                          {m.jurisdiction}
                        </span>
                        <span className="text-xs text-zinc-500">{stageLabel(m.current_stage)}</span>
                        {m.archived && (
                          <span className="text-[11px] px-1.5 py-0.5 rounded bg-zinc-200 text-zinc-600">
                            Archived
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-3 text-xs text-zinc-500 flex-wrap">
                        {m.client_name && <span>Client: <span className="text-zinc-700">{m.client_name}</span></span>}
                        {m.client_reference && <span>Ref: <span className="text-zinc-700">{m.client_reference}</span></span>}
                        {m.invention_title && <span>Invention: <span className="text-zinc-700">{m.invention_title}</span></span>}
                        {m.technology_area && <span>Tech: <span className="text-zinc-700">{m.technology_area}</span></span>}
                        {m.filing_deadline && <span>Filing: {fmtDate(m.filing_deadline)}</span>}
                        <span><Clock size={10} className="inline mr-1" />{fmtDate(m.updated_at)}</span>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      {/* Phase 4G — Drawings entry on the matter card.
                          Only renders once the matter's claims are
                          frozen (matter.claims_frozen_at non-null) AND
                          App.js passed the onOpenDrawings callback. */}
                      {m.claims_frozen_at && onOpenDrawings && (
                        <button
                          className={outlineBtn}
                          onClick={async () => {
                            await loadMatter(m.id);
                            onOpenDrawings();
                          }}
                          disabled={busy}
                          title="Open the Drawing workbench for this matter."
                          data-testid={`matters-drawings-${m.id}`}
                        >
                          <span className="text-xs font-medium">Drawings</span>
                        </button>
                      )}
                      <button
                        className={outlineBtn}
                        onClick={() => setEditingId(isEditing ? null : m.id)}
                        disabled={busy}
                        title="Edit matter"
                        data-testid={`matters-edit-${m.id}`}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        className={outlineBtn}
                        onClick={() => onArchive(m)}
                        disabled={busy}
                        title={m.archived ? 'Unarchive' : 'Archive'}
                        data-testid={`matters-archive-${m.id}`}
                      >
                        {m.archived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                      </button>
                      <button
                        className={
                          armed
                            ? 'inline-flex h-9 items-center justify-center gap-1 rounded-sm bg-red-600 text-white hover:bg-red-700 px-3 text-sm font-medium disabled:opacity-50 transition-colors'
                            : 'inline-flex h-9 items-center justify-center gap-1 rounded-sm border border-red-200 bg-white text-red-600 hover:bg-red-50 px-3 text-sm font-medium disabled:opacity-50 transition-colors'
                        }
                        onClick={() => onDelete(m)}
                        onBlur={() => setConfirmDeleteId(cur => (cur === m.id ? null : cur))}
                        disabled={busy}
                        title={armed ? 'Click again to confirm' : 'Delete matter'}
                        data-testid={`matters-delete-${m.id}`}
                      >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {armed && !busy && <span>Confirm</span>}
                      </button>
                    </div>
                  </div>
                  {isEditing && (
                    <EditableRow
                      matter={m}
                      onClose={() => setEditingId(null)}
                      onPatched={(updated) => {
                        setMatters(prev => prev.map(x => x.id === updated.id ? { ...x, ...updated } : x));
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && total > (query.per_page || 20) && (
          <div
            className="mt-6 flex items-center justify-between text-sm text-zinc-600"
            data-testid="matters-pagination"
          >
            <div>
              Showing {matters.length} of {total}
              {' · page '} {query.page || 1} / {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                className={outlineBtn}
                disabled={(query.page || 1) <= 1}
                onClick={() => setField('page', (query.page || 1) - 1)}
              >
                Previous
              </button>
              <button
                className={outlineBtn}
                disabled={(query.page || 1) >= totalPages}
                onClick={() => setField('page', (query.page || 1) + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
